// Phase G — ISO-week helpers. Plain JS, no external deps. ISO 8601:
// - Weeks start on Monday.
// - Week 1 is the one containing the year's first Thursday.
// All inputs/outputs use the user's LOCAL date (no timezone juggling) so
// the YYYY-MM-DD strings match the booking_date the Jobs filter sends.

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function toLocalYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Monday of the ISO week containing `d`. Returns a new Date at 00:00 local. */
export function startOfIsoWeek(d) {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // JS getDay(): Sun=0 .. Sat=6. ISO wants Mon=1..Sun=7 -> offset.
  const day = out.getDay() || 7; // Sun -> 7
  if (day !== 1) out.setDate(out.getDate() - (day - 1));
  return out;
}

export function endOfIsoWeek(d) {
  const s = startOfIsoWeek(d);
  s.setDate(s.getDate() + 6);
  s.setHours(23, 59, 59, 999);
  return s;
}

/** Returns { year, week } per ISO 8601 (note: late-Dec dates can roll into next year's W1). */
export function isoWeekOf(d) {
  // Algorithm: shift to the Thursday of the same ISO week, take year of
  // that Thursday, then count weeks since the first Monday of that year.
  const dt = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = dt.getDay() || 7; // Sun -> 7
  dt.setDate(dt.getDate() + 4 - day); // shift to Thursday of this ISO week
  const isoYear = dt.getFullYear();
  const yearStart = new Date(isoYear, 0, 1);
  const week = Math.ceil(((dt - yearStart) / 86400000 + 1) / 7);
  return { year: isoYear, week };
}

/** Format an ISO week key as YYYY-WNN. */
export function formatIsoWeekKey(year, week) {
  return `${year}-W${pad2(week)}`;
}

/** Parse a YYYY-WNN key. Returns null on bad input. */
export function parseIsoWeekKey(s) {
  if (typeof s !== "string") return null;
  const m = s.match(/^(\d{4})-W(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const w = Number(m[2]);
  if (w < 1 || w > 53) return null;
  return { year: y, week: w };
}

/** Monday of ISO week W of year Y. Stable across DST. */
export function dateOfIsoWeek(year, week) {
  // ISO 8601: week 1 is the one with Jan 4.
  const jan4 = new Date(year, 0, 4);
  const day = jan4.getDay() || 7; // Sun -> 7
  const mondayOfWeek1 = new Date(year, 0, 4 - (day - 1));
  const monday = new Date(mondayOfWeek1);
  monday.setDate(monday.getDate() + (week - 1) * 7);
  return monday;
}

/** Number of ISO weeks in a given year (52 or 53). */
export function isoWeeksInYear(year) {
  // Year has 53 ISO weeks iff Jan 1 is Thursday, or Jan 1 is Wednesday in a leap year.
  const jan1 = new Date(year, 0, 1).getDay() || 7;
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  return jan1 === 4 || (isLeap && jan1 === 3) ? 53 : 52;
}

/** Build an array of {key, week, year, start (Date), end (Date), label} for every ISO week of `year`. */
export function isoWeeksInYearList(year) {
  const total = isoWeeksInYear(year);
  const fmt = new Intl.DateTimeFormat("en-IE", { day: "2-digit", month: "short" });
  const out = [];
  for (let w = 1; w <= total; w++) {
    const start = dateOfIsoWeek(year, w);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    out.push({
      key: formatIsoWeekKey(year, w),
      year, week: w,
      start, end,
      label: `Week ${w} · ${fmt.format(start)} – ${fmt.format(end)}`,
    });
  }
  return out;
}
